import {
    CONSUMER_CARD_DEFAULTS,
    DiscoveryEvent,
    EventCard,
    EventClient,
    EventClientProvider,
    FormattedEvent,
    transformDiscoveryEvent,
} from '@eventbrite/event-renderer';
import { usePlacementEventContextAggregator } from '@eventbrite/event-tracker';
import { gettext } from '@eventbrite/i18n';
import React, { useContext, useRef } from 'react';
import { TrackingContext } from '../../context';
import { AffiliateCodes } from '../../types';
import { ShareProps } from '../ShareEvent/ShareEventRoot';
import { SimplifiedOrganizerInfo } from '../_shared/OrganizerInfo/SimplifiedOrganizerInfo';
import { StyleTypes } from '../_shared/OrganizerStats/OrganizerStats';
import './ExpiredEvents.scss';
import { usePromotedEvents } from './usePromotedEvents';

export type ExpiredEventsPageProps = {
    pageEventId: string;
    relatedEvents: DiscoveryEvent[];
    eventName: string;
    organizer: {
        name: string;
        profilePicture?: string;
        url: string;
        id: string;
    };
    isAuthenticated: boolean;
    shareComponentProps: ShareProps;
};

export const ExpiredEventsPage = ({
    relatedEvents,
    eventName,
    organizer,
    isAuthenticated,
    shareComponentProps,
    pageEventId,
}: ExpiredEventsPageProps) => {
    const {
        name: organizerName,
        url: organizerUrl,
        profilePicture: organizerAvatar,
        id: organizerId,
    } = organizer;
    const context = useContext(TrackingContext);

    const { promotedEvents, loaded } = usePromotedEvents(
        pageEventId,
        context.featureFlags?.enable_fe_placement_on_related_events,
    );

    return (
        <div className="expired-event js-expired-related-events">
            <div className="expired-wrapper">
                <div className="container">
                    <div>
                        <span className="expired-badge">
                            {gettext('Event ended')}
                        </span>
                    </div>
                    <div className="expired-info">
                        <h3 className="expired-heading">{eventName}</h3>
                        <button className="js-exit-expired-events expired-view-details">
                            {gettext('View details')}
                        </button>
                    </div>
                    <hr className="expired-divider" />
                    <div>
                        <h4 className="expired-organizer-intro">
                            {gettext(
                                'Follow this organizer to stay informed on future events',
                            )}
                        </h4>
                        <div className="expired-organizer">
                            <SimplifiedOrganizerInfo
                                organizerName={organizerName}
                                organizerAvatar={organizerAvatar}
                                organizerUrl={organizerUrl}
                                organizerId={organizerId}
                                style={StyleTypes.CONDENSED}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <ExpiredEventsList
                {...{
                    relatedEvents,
                    promotedEvents,
                    isAuthenticated,
                    shareComponentProps,
                    promotedEventsLoaded: loaded,
                }}
            />
        </div>
    );
};

const ExpiredEventsList = ({
    relatedEvents,
    promotedEvents,
    isAuthenticated,
    shareComponentProps,
    promotedEventsLoaded,
}: {
    relatedEvents: DiscoveryEvent[];
    promotedEvents: FormattedEvent[];
    isAuthenticated: boolean;
    shareComponentProps: ShareProps;
    promotedEventsLoaded: boolean;
}): JSX.Element => {
    const affCode = AffiliateCodes.AFF_RELATED_EVENTS_EXPIRED;
    const eventClient = new EventClient();
    const allEvents: Array<any> = [...promotedEvents, ...relatedEvents];

    const eventCards = (
        <div className="expired-wrapper expired-wrapper--bg-gray">
            <div className="container">
                <div>
                    <p className="related-events__heading">
                        {gettext('Events you might like')}
                    </p>
                </div>
                <ul className="expired-events-grid">
                    <EventClientProvider client={eventClient}>
                        {allEvents.map((event: any, i: number) => {
                            const shareEventProps = {
                                eventId: event.id,
                                eventName: event.name,
                                eventUrl: event.url,
                                ...shareComponentProps,
                            };
                            return (
                                <li
                                    key={i}
                                    className="expired-events-grid__item"
                                >
                                    <EventCard
                                        {...CONSUMER_CARD_DEFAULTS}
                                        eventData={
                                            event.isPromoted
                                                ? event
                                                : transformDiscoveryEvent(event)
                                        }
                                        affCode={affCode}
                                        key={event.id}
                                        gaCategory="expired-events"
                                        isAuthenticated={isAuthenticated}
                                        shareOptions={shareEventProps}
                                    />
                                </li>
                            );
                        })}
                    </EventClientProvider>
                </ul>
            </div>
        </div>
    );

    return promotedEventsLoaded ? (
        <Trackeable events={allEvents}>{eventCards}</Trackeable>
    ) : (
        eventCards
    );
};

const Trackeable: React.FC<any> = ({ children, events }) => {
    const context = useContext(TrackingContext);
    const domElement = useRef<HTMLDivElement>(null);

    usePlacementEventContextAggregator(
        domElement,
        ({ id, action, isPromoted }) =>
            aggregateContext({
                id,
                action,
                isPromoted,
                context,
                events,
            }),
    );

    return <div ref={domElement}>{children}</div>;
};

const RELATED_EVENTS_PLACEMENT_NAME = 'related_events';
const EXPIRED_EVENTS_PLACEMENT_NAME = 'event_expired_page';

function aggregateContext({ action, id, context, events, isPromoted }: any) {
    const { event, absolutePosition } = getEventFromId(events, id, isPromoted);
    return {
        user: {
            id: context.userId,
            locale: context.locale,
            guestId: context.guestId,
            sessionId: context.sessionId,
        },
        item: { id: id },
        adId: event?.promotedListingMetadata?.adId,
        action: action,
        place: {
            name: RELATED_EVENTS_PLACEMENT_NAME,
            position: absolutePosition,
            page: 1,
            context: {
                related_event_id: context.event?.id,
                is_online: context.event?.isOnline,
            },
            tabKey: '',
            subInterface: {
                name: EXPIRED_EVENTS_PLACEMENT_NAME,
                position: '',
            },
            placementId: event?.promotedListingMetadata?.placementId,
        },
    };
}

function getEventFromId(
    allEvents: DiscoveryEvent[],
    eventIdToFind: string,
    isPromoted: boolean,
) {
    const promotedEvents = allEvents.filter(
        (event: DiscoveryEvent) => event?.promotedListingMetadata,
    );
    const organicEvents = allEvents.filter(
        (event: DiscoveryEvent) => !event?.promotedListingMetadata,
    );
    if (isPromoted) {
        const eventPosition = promotedEvents.findIndex(
            (event) => event?.id === eventIdToFind,
        );
        return {
            event: promotedEvents[eventPosition],
            absolutePosition: eventPosition + 1,
        };
    }
    const eventPosition = organicEvents.findIndex(
        (event) => event?.id === eventIdToFind,
    );
    return {
        event: organicEvents[eventPosition],
        absolutePosition: eventPosition + 1,
    };
}
